// src/utils/incomeCalculations.js
import { getFiscalConfig, DEFAULT_FISCAL_YEAR } from './fiscalConfig';

/**
 * Calcule le revenu imposable selon le type de revenu et l'année fiscale
 * @param {Object} income - L'objet revenu
 * @param {number} year - L'année fiscale (optionnel, défaut = année du revenu ou 2024)
 * @returns {number} Le montant imposable
 */
export function calculateTaxableIncome(income, year = null) {
  const gross = income.grossAmount || income.gross_amount || 0;
  const expenses = income.expenses || 0;
  const incomeType = income.incomeType || income.income_type;
  const fiscalYear = year || income.year || DEFAULT_FISCAL_YEAR;

  const config = getFiscalConfig(fiscalYear);

  switch (incomeType) {
    // Abattement forfaitaire 10% (salaires, chômage)
    case 'SALAIRE':
    case 'CHOMAGE': {
      const { rate, min, max } = config.salaryDeduction;
      const deduction = Math.max(Math.min(gross * rate, max), min);
      return Math.max(gross - deduction, 0);
    }

    // Abattement forfaitaire 10% (retraite - plafonds différents)
    case 'RETRAITE': {
      const { rate, min, max } = config.pensionDeduction;
      const deduction = Math.max(Math.min(gross * rate, max), min);
      return Math.max(gross - deduction, 0);
    }

    // Micro-BIC Vente : 71% abattement
    case 'MICRO_BIC_VENTE':
      return gross * (1 - config.microBIC.vente);

    // Micro-BIC Services : 50% abattement
    case 'MICRO_BIC_SERVICE':
      return gross * (1 - config.microBIC.service);

    // LMNP Micro : 50% abattement
    case 'LMNP_MICRO':
      return gross * (1 - config.lmnpMicro);

    // Micro-BNC : 34% abattement
    case 'MICRO_BNC':
      return gross * (1 - config.microBNC);

    // Micro-foncier : 30% abattement
    case 'FONCIER_MICRO':
      return gross * (1 - config.microFoncier);

    // Régimes réels : brut - charges
    case 'REEL_BIC':
    case 'REEL_BNC':
    case 'LMNP_REEL':
    case 'FONCIER_REEL':
    case 'SOCIETE_IR':
      return Math.max(gross - expenses, 0);

    default:
      return gross;
  }
}

/**
 * Calcule le total imposable pour une liste de revenus
 * @param {Array} incomes - Liste des revenus
 * @param {number} year - L'année fiscale (optionnel)
 * @returns {number} Total imposable
 */
export function calculateTotalTaxableIncome(incomes, year = null) {
  return incomes.reduce((total, income) => {
    const taxable = income.taxableAmount || income.taxable_amount || calculateTaxableIncome(income, year);
    return total + taxable;
  }, 0);
}

/**
 * Retourne la description de l'abattement pour un type de revenu
 * @param {string} incomeType - Type de revenu
 * @param {number} year - L'année fiscale (optionnel)
 * @returns {string} Description de l'abattement
 */
export function getAbatementDescription(incomeType, year = DEFAULT_FISCAL_YEAR) {
  const config = getFiscalConfig(year);

  switch (incomeType) {
    case 'SALAIRE':
    case 'CHOMAGE':
      return `10% (max ${config.salaryDeduction.max.toLocaleString('fr-FR')}€)`;
    case 'RETRAITE':
      return `10% (max ${config.pensionDeduction.max.toLocaleString('fr-FR')}€)`;
    case 'MICRO_BIC_VENTE':
      return `${Math.round(config.microBIC.vente * 100)}%`;
    case 'MICRO_BIC_SERVICE':
    case 'LMNP_MICRO':
      return `${Math.round(config.microBIC.service * 100)}%`;
    case 'MICRO_BNC':
      return `${Math.round(config.microBNC * 100)}%`;
    case 'FONCIER_MICRO':
      return `${Math.round(config.microFoncier * 100)}%`;
    case 'REEL_BIC':
    case 'REEL_BNC':
    case 'LMNP_REEL':
    case 'FONCIER_REEL':
    case 'SOCIETE_IR':
      return 'Charges réelles';
    default:
      return '-';
  }
}

/**
 * Vérifie si le type de revenu nécessite la saisie des charges
 * @param {string} incomeType - Type de revenu
 * @returns {boolean}
 */
export function requiresExpenses(incomeType) {
  return ['REEL_BIC', 'REEL_BNC', 'LMNP_REEL', 'FONCIER_REEL', 'SOCIETE_IR'].includes(incomeType);
}

/**
 * Calcule l'impôt sur le revenu brut (avant décote et réductions)
 * @param {number} taxableIncome - Revenu imposable
 * @param {number} parts - Nombre de parts fiscales
 * @param {number} year - L'année fiscale
 * @returns {number} Impôt brut
 */
export function calculateGrossIncomeTax(taxableIncome, parts, year = DEFAULT_FISCAL_YEAR) {
  const config = getFiscalConfig(year);
  const quotient = taxableIncome / parts;

  let taxPerPart = 0;
  for (const bracket of config.irBrackets) {
    if (quotient > bracket.min) {
      const taxableInBracket = Math.min(quotient, bracket.max) - bracket.min;
      taxPerPart += taxableInBracket * bracket.rate;
    }
  }

  return taxPerPart * parts;
}

/**
 * Applique la décote si applicable
 * @param {number} grossTax - Impôt brut
 * @param {boolean} isCouple - Couple marié/pacsé
 * @param {number} year - L'année fiscale
 * @returns {number} Impôt après décote
 */
export function applyDecote(grossTax, isCouple, year = DEFAULT_FISCAL_YEAR) {
  const config = getFiscalConfig(year);
  const { singleThreshold, coupleThreshold, singleCoef, coupleCoef } = config.decote;

  const threshold = isCouple ? coupleThreshold : singleThreshold;
  const coef = isCouple ? coupleCoef : singleCoef;

  if (grossTax <= threshold) {
    const decote = coef - (grossTax * 0.4525);
    return Math.max(grossTax - decote, 0);
  }

  return grossTax;
}

/**
 * Calcule l'impôt sur les sociétés
 * @param {number} profit - Bénéfice imposable
 * @param {boolean} isPME - Est une PME éligible au taux réduit
 * @param {number} year - L'année fiscale
 * @returns {Object} Détail IS { reducedTax, normalTax, totalTax }
 */
export function calculateCorporateTax(profit, isPME, year = DEFAULT_FISCAL_YEAR) {
  const config = getFiscalConfig(year);
  const { reducedRate, reducedRateLimit, normalRate } = config.corporateTax;

  if (isPME && profit <= reducedRateLimit) {
    return {
      reducedTax: profit * reducedRate,
      normalTax: 0,
      totalTax: profit * reducedRate,
    };
  }

  if (isPME) {
    const reducedTax = reducedRateLimit * reducedRate;
    const normalTax = (profit - reducedRateLimit) * normalRate;
    return {
      reducedTax,
      normalTax,
      totalTax: reducedTax + normalTax,
    };
  }

  return {
    reducedTax: 0,
    normalTax: profit * normalRate,
    totalTax: profit * normalRate,
  };
}

/**
 * Calcule les prélèvements sociaux sur revenus du patrimoine
 * @param {number} income - Revenu du patrimoine
 * @param {number} year - L'année fiscale
 * @returns {Object} Détail prélèvements { csg, crds, solidarite, total }
 */
export function calculateSocialContributions(income, year = DEFAULT_FISCAL_YEAR) {
  const config = getFiscalConfig(year);
  const { csg, crds, prelevementSolidarite, total } = config.socialContributions;

  return {
    csg: income * csg,
    crds: income * crds,
    solidarite: income * prelevementSolidarite,
    total: income * total,
  };
}

/**
 * Calcule le nombre de parts fiscales
 * @param {Object} individual - L'individu avec maritalStatus, childrenCount, singleParent
 * @returns {number} Nombre de parts
 */
export function calculateFiscalParts(individual) {
  let parts = 1; // Base célibataire

  const maritalStatus = individual.maritalStatus || individual.marital_status;
  if (['Marié', 'Pacsé'].includes(maritalStatus)) {
    parts = 2;
  }

  const children = individual.childrenCount || individual.children_count || 0;
  if (children >= 1) parts += 0.5;
  if (children >= 2) parts += 0.5;
  if (children >= 3) parts += (children - 2) * 1;

  const singleParent = individual.singleParent || individual.single_parent;
  if (singleParent && children > 0) {
    parts += 0.5;
  }

  return parts;
}

/**
 * Calcul complet de l'Impôt sur le Revenu
 * @param {Object} individual - L'individu (situation familiale)
 * @param {Array} incomes - Liste des revenus de l'individu
 * @param {number} year - L'année fiscale
 * @returns {Object} Détail complet du calcul IR
 */
export function calculateFullIR(individual, incomes, year = DEFAULT_FISCAL_YEAR) {
  const config = getFiscalConfig(year);

  // 1. Calcul du revenu imposable total
  const yearIncomes = incomes.filter(inc => inc.year === year);
  const totalTaxableIncome = calculateTotalTaxableIncome(yearIncomes, year);

  // 2. Calcul des parts fiscales
  const parts = calculateFiscalParts(individual);
  const maritalStatus = individual.maritalStatus || individual.marital_status;
  const isCouple = ['Marié', 'Pacsé'].includes(maritalStatus);
  const baseParts = isCouple ? 2 : 1;
  const additionalHalfParts = (parts - baseParts) * 2; // Nombre de demi-parts supplémentaires

  // 3. Calcul du quotient familial
  const quotient = totalTaxableIncome / parts;

  // 4. Calcul de l'impôt brut avec quotient familial
  const grossTaxWithQuotient = calculateGrossIncomeTax(totalTaxableIncome, parts, year);

  // 5. Calcul de l'impôt de référence (sans quotient familial, juste parts de base)
  const grossTaxReference = calculateGrossIncomeTax(totalTaxableIncome, baseParts, year);

  // 6. Plafonnement du quotient familial
  const { maxReductionPerHalfPart, singleParentBonus } = config.familyQuotient;
  const singleParent = individual.singleParent || individual.single_parent;
  const children = individual.childrenCount || individual.children_count || 0;

  let maxReduction = additionalHalfParts * maxReductionPerHalfPart;

  // Bonus parent isolé pour la première demi-part
  if (singleParent && children > 0) {
    maxReduction += (singleParentBonus - maxReductionPerHalfPart);
  }

  const actualReduction = grossTaxReference - grossTaxWithQuotient;
  const cappedReduction = Math.min(actualReduction, maxReduction);
  const grossTaxAfterCapping = grossTaxReference - cappedReduction;

  // 7. Application de la décote
  const taxAfterDecote = applyDecote(grossTaxAfterCapping, isCouple, year);

  // 8. Détermination du taux marginal d'imposition (TMI)
  let marginalRate = 0;
  for (const bracket of config.irBrackets) {
    if (quotient > bracket.min) {
      marginalRate = bracket.rate;
    }
  }

  // 9. Calcul du taux moyen d'imposition
  const averageRate = totalTaxableIncome > 0 ? (taxAfterDecote / totalTaxableIncome) : 0;

  return {
    // Revenus
    year,
    totalGrossIncome: yearIncomes.reduce((sum, inc) => sum + (inc.grossAmount || inc.gross_amount || 0), 0),
    totalTaxableIncome,
    incomeCount: yearIncomes.length,

    // Quotient familial
    parts,
    baseParts,
    additionalHalfParts,
    quotient: Math.round(quotient),

    // Calcul IR
    grossTaxReference: Math.round(grossTaxReference),
    grossTaxWithQuotient: Math.round(grossTaxWithQuotient),
    quotientReduction: Math.round(actualReduction),
    maxQuotientReduction: Math.round(maxReduction),
    isCapped: actualReduction > maxReduction,
    cappedReduction: Math.round(cappedReduction),
    grossTaxAfterCapping: Math.round(grossTaxAfterCapping),

    // Décote
    decoteApplied: grossTaxAfterCapping !== taxAfterDecote,
    decoteAmount: Math.round(grossTaxAfterCapping - taxAfterDecote),

    // Résultat final
    finalTax: Math.round(taxAfterDecote),

    // Taux
    marginalRate,
    marginalRatePercent: Math.round(marginalRate * 100),
    averageRate,
    averageRatePercent: Math.round(averageRate * 100 * 100) / 100,

    // Situation familiale
    isCouple,
    childrenCount: children,
    singleParent
  };
}
